import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { Seo } from "../components/site/Seo";
import { SectionLabel } from "../components/site/SectionLabel";
import { ArticleBlocks } from "../components/site/ArticleBlocks";
import { TechnicalDisclaimer } from "../components/site/TechnicalDisclaimer";
import { getPuArticle, getSilArticle, getCalculator } from "../data/technical";
import { getAdvisory } from "../data/advisory";
import NotFound from "./NotFound";

// kind: "pu" | "sil"
export default function TechArticle({ kind }) {
  const { slug } = useParams();
  const a = kind === "pu" ? getPuArticle(slug) : getSilArticle(slug);
  if (!a) return <NotFound />;

  const base = kind === "pu" ? "/technical-hub/polyurethane" : "/technical-hub/silicones";
  const centreLabel = kind === "pu" ? "Polyurethane" : "Silicones";
  const getArt = kind === "pu" ? getPuArticle : getSilArticle;
  const rel = a.related || {};
  const relatedArticles = (rel.articles || []).map(getArt).filter(Boolean);
  const calc = rel.calc ? getCalculator(rel.calc) : null;
  const advisory = rel.advisory ? getAdvisory(rel.advisory) : null;

  return (
    <div>
      <Seo
        title={`${a.title} — MATRIVON`}
        description={a.summary}
        path={`${base}/${a.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: a.title,
          description: a.summary,
          dateModified: a.reviewed,
          author: { "@type": "Organization", name: "MATRIVON" },
          publisher: { "@type": "Organization", name: "MATRIVON" },
        }}
      />
      <PageHero
        variant="light"
        label={`${centreLabel} · ${a.section}`}
        title={a.title}
        intro={a.summary}
        crumbs={[
          { label: "Technical Hub", to: "/technical-hub" },
          { label: centreLabel, to: base },
          { label: a.title },
        ]}
      />

      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[2fr_1fr]">
          <article>
            <div className="mb-6 text-xs text-brand-muted">Last reviewed: {a.reviewed}</div>
            <ArticleBlocks blocks={a.blocks} />
            <div className="mt-10">
              <TechnicalDisclaimer />
            </div>
          </article>

          <aside className="space-y-8 lg:sticky lg:top-24 lg:self-start">
            {calc && (
              <div className="border border-brand-blue/20 bg-brand-blue/5 p-6">
                <SectionLabel>Related calculator</SectionLabel>
                <h3 className="font-heading text-lg font-bold text-brand-ink">{calc.title}</h3>
                <Link
                  to={`/technical-hub/calculators/${calc.slug}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:gap-2.5"
                >
                  Open calculator <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}

            {relatedArticles.length > 0 && (
              <div className="border border-brand-line bg-white p-6">
                <SectionLabel>Related articles</SectionLabel>
                <ul className="space-y-3">
                  {relatedArticles.map((r) => (
                    <li key={r.slug}>
                      <Link
                        to={`${base}/${r.slug}`}
                        className="group flex items-start gap-2 text-sm font-medium text-brand-ink hover:text-brand-blue"
                      >
                        <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" /> {r.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {advisory && (
              <div className="border border-brand-line bg-brand-ink p-6 text-white">
                <SectionLabel light>Relevant advisory</SectionLabel>
                <h3 className="font-heading text-lg font-bold">{advisory.title}</h3>
                <Link
                  to={`/advisory/${advisory.slug}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:gap-2.5"
                >
                  Explore advisory <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
